import { Input, Select } from 'antd';
import type { RefSelectProps } from 'antd/es/select';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { DISABLED_TEXT_CLASS } from './styles';

const YEAR_RANGE = 100;

const pad = (value: string) => value.padStart(2, '0');

/**
 * 생일 값을 연/월/일 문자열로 분해한다.
 * 'YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss.sssZ', Date, 미완성 값('YYYY-MM-') 모두 처리한다.
 */
const parseBirthday = (value?: string | Date) => {
  if (!value) return { year: '', month: '', day: '' };

  if (value instanceof Date) {
    return {
      year: String(value.getFullYear()),
      month: String(value.getMonth() + 1),
      day: String(value.getDate()),
    };
  }

  const [year = '', month = '', day = ''] = value.split(/[-T]/);
  return {
    year,
    month: month ? String(Number(month)) : '',
    day: day ? String(Number(day)) : '',
  };
};

/**
 * 세 값 중 하나라도 비어있으면 미완성 문자열('1990-01-')을 반환한다.
 * 저장 시 유효성 검사에서 걸러내기 위함이다.
 */
const buildBirthday = (year: string, month: string, day: string) => {
  if (!year && !month && !day) return '';
  return `${year}-${month ? pad(month) : ''}-${day ? pad(day) : ''}`;
};

/** 연/월이 정해지지 않았다면 최대값인 31일을 기준으로 한다. */
const getLastDay = (year: string, month: string) => {
  if (!month) return 31;
  return new Date(Number(year) || 2000, Number(month), 0).getDate();
};

interface BirthdayInputProps {
  value?: string | Date;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: boolean;
}

/**
 * react-hook-form 이 `_f.ref` 에 기대하는 최소 형태.
 * 검증 실패 시 `shouldFocus` 가 focus()를 호출하고, select()는 setFocus 경로에서 쓰인다.
 */
export interface BirthdayInputRef {
  focus: () => void;
  select: () => void;
}

const BirthdayInput = forwardRef<BirthdayInputRef, BirthdayInputProps>(
  ({ value, onChange, disabled, error }, ref) => {
    const initial = parseBirthday(value);
    const [year, setYear] = useState(initial.year);
    const [month, setMonth] = useState(initial.month);
    const [day, setDay] = useState(initial.day);

    // 직접 올려보낸 값이 되돌아오는 경우를 걸러내기 위해 마지막 전달값을 기억한다.
    const emittedRef = useRef<string>();

    // 검증 실패 시 첫 오류 항목으로 이동할 수 있도록 연도 선택으로 포커스를 넘긴다.
    const yearRef = useRef<RefSelectProps>(null);
    useImperativeHandle(
      ref,
      () => ({
        focus: () => yearRef.current?.focus(),
        select: () => {},
      }),
      [],
    );

    // 조회 완료 / 수정 취소 등 외부에서 값이 바뀌면 입력 상태를 다시 맞춘다.
    useEffect(() => {
      const incoming = typeof value === 'string' ? value : undefined;
      if (incoming !== undefined && incoming === emittedRef.current) return;

      const parsed = parseBirthday(value);
      setYear(parsed.year);
      setMonth(parsed.month);
      setDay(parsed.day);
    }, [value]);

    const emit = (nextYear: string, nextMonth: string, nextDay: string) => {
      const next = buildBirthday(nextYear, nextMonth, nextDay);
      emittedRef.current = next;
      onChange(next);
    };

    const currentYear = new Date().getFullYear();
    const yearOptions = Array.from({ length: YEAR_RANGE + 1 }, (_, index) => {
      const optionYear = String(currentYear - index);
      return { value: optionYear, label: `${optionYear}년` };
    });
    const monthOptions = Array.from({ length: 12 }, (_, index) => ({
      value: String(index + 1),
      label: `${index + 1}월`,
    }));

    // 연/월이 바뀌어 일자가 말일을 넘기면 말일로 맞춘다. (예: 3월 31일 → 2월 선택)
    const clampDay = (nextYear: string, nextMonth: string) => {
      if (!day) return '';
      const lastDay = getLastDay(nextYear, nextMonth);
      return Number(day) > lastDay ? String(lastDay) : day;
    };

    const handleYearChange = (nextYear: string) => {
      const nextDay = clampDay(nextYear, month);
      setYear(nextYear);
      setDay(nextDay);
      emit(nextYear, month, nextDay);
    };

    const handleMonthChange = (nextMonth: string) => {
      const nextDay = clampDay(year, nextMonth);
      setMonth(nextMonth);
      setDay(nextDay);
      emit(year, nextMonth, nextDay);
    };

    const handleDayChange = (input: string) => {
      const nextDay = input.replace(/[^0-9]/g, '').slice(0, 2);
      setDay(nextDay);
      emit(year, month, nextDay);
    };

    // 입력이 끝난 시점에 1 ~ 말일 범위로 보정한다.
    const handleDayBlur = () => {
      if (!day) return;
      const lastDay = getLastDay(year, month);
      const nextDay = String(Math.min(Math.max(Number(day), 1), lastDay));
      if (nextDay === day) return;
      setDay(nextDay);
      emit(year, month, nextDay);
    };

    // antd Select(size=large)는 height 40px가 고정되어 있어, p-3 Input(50px) 높이에 맞추기 위해 덮어쓴다.
    const selectClassName = `w-full !h-full [&_.ant-select-selector]:!rounded ${
      disabled ? '[&_.ant-select-selection-item]:!text-gray7' : ''
    }`;

    return (
      <div className="grid grid-cols-3 gap-x-2">
        <Select
          ref={yearRef}
          size="large"
          placeholder="연도"
          value={year || undefined}
          options={yearOptions}
          onChange={handleYearChange}
          disabled={disabled}
          status={error ? 'error' : undefined}
          className={selectClassName}
        />
        <Select
          size="large"
          placeholder="월"
          value={month || undefined}
          options={monthOptions}
          onChange={handleMonthChange}
          disabled={disabled}
          status={error ? 'error' : undefined}
          className={selectClassName}
        />
        <Input
          type="text"
          inputMode="numeric"
          size="large"
          placeholder="숫자만 입력"
          value={day}
          onChange={(e) => handleDayChange(e.target.value)}
          onBlur={handleDayBlur}
          disabled={disabled}
          status={error ? 'error' : undefined}
          suffix={<span className="text-gray6">일</span>}
          className={`rounded p-3 ${disabled ? DISABLED_TEXT_CLASS : ''}`}
          autoComplete="off"
        />
      </div>
    );
  },
);

BirthdayInput.displayName = 'BirthdayInput';

export default BirthdayInput;
